/**
 * f12.1: pure formatting for the review screen (BR-F12.1-7).
 *
 * Amounts arrive as decimal STRINGS and are formatted here without float
 * arithmetic: rounding to whole currency units is done on the digits
 * (half-even, as the server rounds), then grouped. Colour decisions are pure
 * functions returning the prototype's own classes -- never a hex value.
 */

export interface MoneyLike {
  amount: string;
  currency_code: string;
}

export const EMPTY = '—';

const SYMBOLS: Readonly<Record<string, string>> = { USD: '$', EUR: '€', GBP: '£' };

interface Parsed {
  negative: boolean;
  integer: string;
  fraction: string;
}

function parse(amount: string): Parsed | null {
  const match = /^\s*([+-]?)(\d*)(?:\.(\d*))?\s*$/.exec(amount);
  if (!match || (!match[2] && !match[3])) return null;
  return { negative: match[1] === '-', integer: match[2] || '0', fraction: match[3] ?? '' };
}

function incrementDigits(digits: string): string {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i] === '9') {
      out[i] = '0';
    } else {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
  }
  return `1${out.join('')}`;
}

/** Round a decimal string to a whole number, half-even, returning the digits. */
export function roundWhole(amount: string): { negative: boolean; digits: string } | null {
  const parsed = parse(amount);
  if (!parsed) return null;
  let digits = parsed.integer.replace(/^0+(?=\d)/, '');
  const first = parsed.fraction.charAt(0);
  const rest = parsed.fraction.slice(1);
  const lastDigitOdd = Number(digits.charAt(digits.length - 1)) % 2 === 1;
  const roundUp =
    first > '5' || (first === '5' && (/[1-9]/.test(rest) || lastDigitOdd));
  if (first && roundUp) digits = incrementDigits(digits);
  const zero = /^0+$/.test(digits);
  return { negative: parsed.negative && !zero, digits };
}

function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** The sign of a decimal string: -1, 0 or 1 -- no float involved. */
export function signOf(amount: string | null | undefined): -1 | 0 | 1 {
  if (amount == null) return 0;
  const parsed = parse(amount);
  if (!parsed) return 0;
  const nonZero = /[1-9]/.test(parsed.integer + parsed.fraction);
  if (!nonZero) return 0;
  return parsed.negative ? -1 : 1;
}

/** `$9,069,001`, `-€160,421`, `CHF 1,200`. Null renders "—". */
export function formatMoney(money: MoneyLike | null | undefined, options: { signed?: boolean } = {}): string {
  if (!money) return EMPTY;
  const rounded = roundWhole(money.amount);
  if (!rounded) return EMPTY;
  const symbol = SYMBOLS[money.currency_code];
  const body = symbol ? `${symbol}${group(rounded.digits)}` : `${money.currency_code} ${group(rounded.digits)}`;
  if (rounded.negative) return `-${body}`;
  if (options.signed && rounded.digits !== '0' && !/^0+$/.test(rounded.digits)) return `+${body}`;
  return body;
}

/** The delta cell text: explicit `+` when positive (BR-F12.1-7). */
export function formatDelta(money: MoneyLike | null | undefined): string {
  return formatMoney(money, { signed: true });
}

/** Negative -> red, positive -> green, zero or null -> neutral. */
export function deltaClass(money: MoneyLike | null | undefined): 'delta-down' | 'delta-up' | 'delta-flat' {
  const sign = signOf(money?.amount);
  if (sign < 0) return 'delta-down';
  if (sign > 0) return 'delta-up';
  return 'delta-flat';
}

/** H -> red, M -> amber, L -> green; no badge without a rating. */
export function riskClass(code: string | null | undefined): string | null {
  switch (code) {
    case 'H':
      return 'pill-danger';
    case 'M':
      return 'pill-warning';
    case 'L':
      return 'pill-success';
    default:
      return null;
  }
}

/** A money input's text as the PATCH sends it: trimmed, empty -> null (clears the field). */
export function amountInput(text: string): string | null {
  const trimmed = text.trim().replace(/,/g, '');
  return trimmed === '' ? null : trimmed;
}
